import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { DictTypes } from '@/constants';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { cleanNameValue, onlyDigits } from '@/services/formatter';
import { handleError } from '@/services/handleError';
import { autoCredMapper } from '@/services/mappers/autoCredMapper';
import { useAppDispatch, useAppSelector } from '@/store';
import { setPageTitle } from '@/store/newOrder.slice';
import type { TDataSource } from '@/types/dataSource';

import useFilters from './useFilters';

const useRequests = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { dicts } = useAppSelector((state) => state.dict);
  const { userInfo } = useAppSelector((state) => state.userInfo);

  const [filteredArray, setFilteredArray] = useState<TDataSource[]>([]);
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [isTableDataLoading, setIsTableLoading] = useState<boolean>(false);

  const filters = useFilters();
  const {
    paginationData,
    selectedUser,
    searchValue,
    debouncedSearchValue,
    filterParamValues: {
      activeOnlineRequest,
      activeParamBanks,
      activeParamDealers,
      activeParamModels,
      activeParamPeriod,
      activeParamStatuses
    }
  } = filters;

  const autoType = useMemo(() => {
    return searchParams.get('auto-type');
  }, [searchParams]);

  const onlineRequest = useMemo(() => {
    return searchParams.get('online-request');
  }, [searchParams]);

  const dictDealers = dicts?.[DictTypes.Dealers]?.content;
  const dictBanks = dicts?.[DictTypes.Banks]?.content;

  const userSelectOptions = [
    { value: userInfo?.uuid || '', label: 'Мои заявки' },
    { value: '-1', label: 'Все заявки' }
  ];

  const dealerOptions = useMemo(
    () =>
      dictDealers
        ? dictDealers.map((dealer) => ({
            value: String(dealer.code),
            label: dealer.name
          }))
        : [],
    [dictDealers]
  );

  const bankOptions = useMemo(
    () =>
      dictBanks
        ? dictBanks
            .filter((bank) => bank.is_active)
            .map((bank) => ({
              value: bank.uuid,
              label: bank.fullname
            }))
        : [],
    [dictBanks]
  );

  const dealerEffectDependency = useMemo(() => {
    const selectedDealers = searchParams.get('dealers')?.split(',');
    if (!selectedDealers) return '-1';
    return selectedDealers.join('');
  }, [searchParams]);

  const bankEffectDependency = useMemo(() => {
    const selectedBanks = searchParams.get('banks')?.split(',');
    if (!selectedBanks) return '';
    return selectedBanks.join('');
  }, [searchParams]);

  const statusEffectDependency = useMemo(() => {
    const selectedStatuses = searchParams.get('statuses')?.split(',');
    if (!selectedStatuses) return '';
    return selectedStatuses.join('');
  }, [searchParams]);

  const modelEffectDependency = useMemo(() => {
    const selectedModels = searchParams.get('models')?.split(',');
    if (!selectedModels) return '-1';
    return selectedModels.join('');
  }, [searchParams]);

  const periodEffectDependency = useMemo(() => {
    const selectedPeriod = searchParams.get('period')?.split(',');
    if (!selectedPeriod || selectedPeriod.length !== 2) return '';
    return selectedPeriod.join('');
  }, [searchParams]);

  const getRequestsPerPage = async () => {
    const activeDealers = activeParamDealers.map((item) => item.uuid);
    const activeModels = activeParamModels.map((item) => item.value);

    if (activeDealers.length === 0) return;
    if (!selectedUser || !autoType) return;
    setIsTableLoading(true);

    try {
      const formattedNameSearchValue = cleanNameValue(searchValue);
      const formattedPhoneSearchValue = onlyDigits(searchValue);
      let searchType: 'fio' | 'iinTelNum' | 'empty' = 'empty';

      if (formattedNameSearchValue === '' && formattedPhoneSearchValue === '') {
        searchType = 'empty';
      } else if (
        formattedNameSearchValue !== '' &&
        formattedPhoneSearchValue === ''
      ) {
        searchType = 'fio';
      } else {
        searchType = 'iinTelNum';
      }

      const data = await autoCreditApi.getRequests({
        city_uuid: '-1',
        repayment_type_uuid: '-1',
        is_online_request:
          activeOnlineRequest !== 'all'
            ? activeOnlineRequest === 'online'
            : '-1',
        dealer_uuid:
          activeDealers.length < dealerOptions.length ? activeDealers : '-1',
        bank_uuid: activeParamBanks.map((item) => item.value),
        model_uuid: activeModels.length > 0 ? activeModels : '-1',
        date_from: activeParamPeriod[0] || '-1',
        date_until: activeParamPeriod[1] || '-1',
        status: activeParamStatuses.map((item) => item.value),
        status_uuid: '-1',
        user_uuid: selectedUser !== '-1' ? [selectedUser] : '-1',
        auto_type_uuid: [autoType],
        search_query: {
          type: searchType === 'empty' ? 'iinTelNum' : searchType,
          value:
            searchType === 'iinTelNum' ? formattedPhoneSearchValue : searchValue
        },
        pagination: {
          page_number: paginationData.currentPage,
          rows_per_page: paginationData.itemsPerPage
        }
      });

      const dataSource: TDataSource[] = autoCredMapper({
        requests: data.data,
        isDetailed: false
      });
      setFilteredArray(dataSource);
      setTotalRequests(data.count);
    } catch (error) {
      handleError(error);
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    dispatch(setPageTitle(''));
  }, []);

  useEffect(() => {
    getRequestsPerPage();
  }, [
    selectedUser,
    dealerEffectDependency,
    bankEffectDependency,
    modelEffectDependency,
    periodEffectDependency,
    statusEffectDependency,
    paginationData.currentPage,
    paginationData.itemsPerPage,
    debouncedSearchValue,
    autoType,
    onlineRequest
  ]);

  return {
    dealerOptions,
    bankOptions,
    filteredArray,
    totalRequests,
    isTableDataLoading,
    filters,
    getRequestsPerPage,
    userSelectOptions
  };
};

export default useRequests;
